import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useNavigate,
  Outlet,
  useOutletContext
} from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import FilterBar from './components/FilterBar';
import Gallery from './components/Gallery';
import PrivacyPolicy from './components/PrivacyPolicy';
import Footer from './components/Footer';
import { FontLoadingIndicator } from './components/FontLoadingIndicator';
import { TattooData, CategoryData, Tattoo } from './types';
import { validateData, ValidationError } from './utils/validators';
import './styles/style.css';
import { useEffect as useEffectAlias } from 'react';

// Lazy load the Editor component for better performance
const Editor = lazy(() => import('./components/EditorRefactored'));

function GalleryPage({
  tattooData,
  categoriesData,
  activeCategory,
  handleCategorySelect
}: {
  tattooData: TattooData;
  categoriesData: CategoryData;
  activeCategory: string | null;
  handleCategorySelect: (category: string | null) => void;
}) {
  const navigate = useNavigate();

  const handleTattooSelect = (tattoo: Tattoo) => {
    // Preload editor when user shows intent to use it
    import('./components/EditorRefactored').catch(() => {
      // Silently fail - not critical
    });
    
    localStorage.setItem('selectedTattoo', JSON.stringify(tattoo));
    navigate('/editor');
  };

  return (
    <>
      <div className="samples">
        <div className="container">
          <FilterBar
            categories={categoriesData.categories}
            activeCategory={activeCategory}
            onCategorySelect={handleCategorySelect}
          />
          <Gallery
            tattoos={tattooData.tattoos}
            activeCategory={activeCategory}
            onTattooSelect={handleTattooSelect}
          />
        </div>
      </div>
    </>
  );
}

function AppLayout() {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [tattooData, setTattooData] = useState<TattooData | null>(null);
  const [categoriesData, setCategoriesData] = useState<CategoryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [tattoosRes, categoriesRes] = await Promise.all([
          fetch(`${process.env.PUBLIC_URL}/content/data/tattoos.json`),
          fetch(`${process.env.PUBLIC_URL}/content/data/categories.json`)
        ]);

        if (!tattoosRes.ok || !categoriesRes.ok) {
          throw new Error('Failed to load data');
        }

        const [tattoos, categories] = await Promise.all([
          tattoosRes.json(),
          categoriesRes.json()
        ]);

        // Validate data before setting state
        try {
          validateData(categories, tattoos);
        } catch (validationError) {
          if (validationError instanceof ValidationError) {
            throw new Error(`Invalid data format: ${validationError.message}`);
          }
          throw validationError;
        }

        setTattooData(tattoos);
        setCategoriesData(categories);
        
        // Set first category as default
        if (categories.categories && categories.categories.length > 0) {
          setActiveCategory(categories.categories[0].id);
        }
      } catch (err) {
        console.error('Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleCategorySelect = (category: string | null) => {
    // setActiveCategory(category === activeCategory ? null : category);
    setActiveCategory(category);
  };

  // Preload Editor component after homepage loads for better UX
  useEffect(() => {
    // Only preload if we're on homepage and data has loaded
    if (location.pathname === '/' && tattooData && categoriesData) {
      // Preload editor after 2 seconds to give homepage time to render
      const preloadTimer = setTimeout(() => {
        
        import('./components/EditorRefactored')
          // .then(() => {
          //   console.log('✅ Editor component preloaded successfully');
          // })
          // .catch((error) => {
          //   console.warn('⚠️ Editor preload failed (not critical):', error);
          // });
      }, 2000);

      return () => clearTimeout(preloadTimer);
    }
  }, [location.pathname, tattooData, categoriesData]);

  // Track SPA route changes for GA4 and TikTok
  useEffect(() => {
    try {
      // Google Analytics 4
      if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'page_view', {
          page_location: window.location.href,
          page_path: location.pathname + location.search,
          page_title: document.title
        });
      }

      // TikTok Pixel
      if (typeof window !== 'undefined' && (window as any).ttq && typeof (window as any).ttq.page === 'function') {
        (window as any).ttq.page();
      }
    } catch (e) {
      // No-op: analytics should never break the app
    }
  }, [location.pathname, location.search]);

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (error || !tattooData || !categoriesData) {
    return (
      <div className="error">
        <p>Error loading content:</p>
        <p>{error || 'Failed to load data'}</p>
        <p>Please check that the JSON files are properly formatted.</p>
      </div>
    );
  }

  return (
    <div className="page">
      <Header />
      {location.pathname === '/' && <Hero />}
      <Outlet context={{ tattooData, categoriesData, activeCategory, handleCategorySelect }} />
      <Footer />
      <FontLoadingIndicator showProgress={false} />
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: <GalleryPageWrapper />,
      },
      {
        path: "editor",
        element: (
          <Suspense fallback={<div className="loading">Loading Editor...</div>}>
            <Editor />
          </Suspense>
        ),
      },
      {
        path: "privacy-policy",
        element: <PrivacyPolicy />,
      },
    ],
  },
]);

function GalleryPageWrapper() {
  const { tattooData, categoriesData, activeCategory, handleCategorySelect } = useOutletContext<any>();
  return (
    <GalleryPage
      tattooData={tattooData}
      categoriesData={categoriesData}
      activeCategory={activeCategory}
      handleCategorySelect={handleCategorySelect}
    />
  );
}


function App() {
  return <RouterProvider router={router} />;
}

export default App;
